import { useEffect, useState } from 'react';
import { useVgpsRuntime } from 'livekit_web/react';
import type { VgpsLiveSession } from 'livekit_web';
import { leaseLiveSession } from '../session_registry';

export interface UseLiveSessionParams {
  deviceSerial: string;
  channelId: string;
  qualityHint?: 'uhd' | 'hd' | 'sd' | 'auto';
  /**
   * Increment to force the hook to release the current lease and acquire a
   * fresh one (e.g. user clicked "reclaim" after their session was evicted
   * by another tab). Each new value triggers exactly one re-lease cycle.
   */
  reopenKey?: number;
}

export interface UseLiveSessionResult {
  session: VgpsLiveSession | null;
  error: Error | null;
  ready: boolean;
}

export function useLiveSession({
  deviceSerial,
  channelId,
  qualityHint,
  reopenKey,
}: UseLiveSessionParams): UseLiveSessionResult {
  const runtime = useVgpsRuntime();
  const [session, setSession] = useState<VgpsLiveSession | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!runtime || !deviceSerial || !channelId) {
      setSession(null);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    let releaseRef: (() => Promise<void>) | null = null;

    setSession(null);
    setError(null);

    console.log(`[useLiveSession] acquiring lease dev=${deviceSerial} ch=${channelId} quality=${qualityHint}`);

    void (async () => {
      try {
        const lease = await leaseLiveSession(runtime, { deviceSerial, channelId, qualityHint });
        releaseRef = lease.release;

        if (cancelled) {
          console.log(`[useLiveSession] cancelled after lease acquired, releasing dev=${deviceSerial} ch=${channelId}`);
          await lease.release();
          return;
        }

        console.log(`[useLiveSession] lease acquired sessionId=${lease.session.sessionId} dev=${deviceSerial} ch=${channelId}`);
        setSession(lease.session);
      } catch (err) {
        if (!cancelled) {
          console.error(`[useLiveSession] lease failed dev=${deviceSerial} ch=${channelId}`, err);
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      }
    })();

    return () => {
      console.log(`[useLiveSession] releasing lease dev=${deviceSerial} ch=${channelId}`);
      cancelled = true;
      setSession(null);
      if (releaseRef) void releaseRef();
    };
  }, [runtime, deviceSerial, channelId, qualityHint, reopenKey]);

  return { session, error, ready: session !== null };
}
